import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { ChevronDownIcon } from "@ui/components/Icon/ChevronDownIcon";
import { ScrollArea } from "@ui/components/ScrollArea";
import { tv } from "@ui/lib/tv";
import { Button, Button as RACSelectButton } from "@ui/primitives/Button";
import { CalendarMonthPicker, CalendarYearPicker } from "@ui/primitives/Calendar";
import { ListBox as RACListBox, ListBoxItem as RACListBoxItem } from "@ui/primitives/ListBox";
import { Popover as RACPopover } from "@ui/primitives/Popover";
import { Select as RACSelect, SelectValue as RACSelectValue } from "@ui/primitives/Select";
import type { Key } from "@ui/primitives/shared";

const calendarMonthYearNav = tv({
  slots: {
    header: "flex h-8 items-center justify-between gap-1",
    navButton: "flex shrink-0 items-center justify-center text-neutral-600 [&_svg]:size-5",
    pickers: "flex flex-1 items-center justify-center gap-1",
    selectRoot: "flex",
    selectTrigger: [
      "flex items-center gap-0.5 rounded-lg px-2 py-1 outline-none",
      "font-semibold text-base/6 text-neutral-600",
      "hover:bg-neutral-50",
      "data-[focus-visible]:shadow-[0_0_0_2px_var(--color-blue-400)]",
    ],
    selectChevron: "size-4 shrink-0 text-neutral-400 transition-transform group-data-[open]:rotate-180",
    selectPopover: [
      // w-max sizes the popover to its widest item (e.g. "September") so long
      // month names aren't clipped; min-w keeps it at least as wide as the trigger.
      // overflow-hidden + rounded clips cleanly; the inner list owns the scroll so
      // the scrollbar stays inside the padding, away from the rounded corners.
      "w-max min-w-(--trigger-width) overflow-hidden rounded-xl bg-white p-1 outline-none",
      "shadow-[0_3px_10px_0_rgba(0,0,0,0.1),0_1px_0_0_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.05)]",
      "entering:fade-in entering:zoom-in-95 entering:animate-in entering:duration-100",
      "exiting:fade-out exiting:zoom-out-95 exiting:animate-out exiting:duration-75",
    ],
    // Each item is 2.5rem tall (text-base/6 = 1.5rem + py-2 = 1rem); padding-based
    // height so flex children don't shrink. max-height = exactly 7 rows, so the list
    // always shows a whole number of entries — never a half-cut row.
    selectListRoot: "max-h-[calc(7*2.5rem)]",
    selectList: "flex flex-col outline-none",
    selectItem: [
      "flex shrink-0 cursor-pointer items-center whitespace-nowrap rounded-lg px-3 py-2 text-base/6 text-neutral-700 outline-none",
      "data-[focused]:bg-neutral-50 data-[hovered]:bg-neutral-50",
      "data-[selected]:bg-neutral-100 data-[selected]:font-semibold",
    ],
  },
});

interface PickerItem {
  id: number;
  formatted: string;
}

interface CalendarHeaderSelectProps {
  ariaLabel: string;
  items: PickerItem[];
  selectedKey: Key;
  onSelectionChange: (key: Key | null) => void;
}

function CalendarHeaderSelect({ ariaLabel, items, selectedKey, onSelectionChange }: CalendarHeaderSelectProps) {
  const styles = calendarMonthYearNav();

  return (
    <RACSelect
      aria-label={ariaLabel}
      className={`group ${styles.selectRoot()}`}
      onSelectionChange={onSelectionChange}
      selectedKey={selectedKey}
    >
      <RACSelectButton className={styles.selectTrigger()}>
        <RACSelectValue />
        <ChevronDownIcon className={styles.selectChevron()} />
      </RACSelectButton>
      <RACPopover className={styles.selectPopover()}>
        <ScrollArea
          className={styles.selectListRoot()}
          inset="none"
          render={
            <RACListBox items={items}>
              {(item) => (
                <RACListBoxItem className={styles.selectItem()} id={item.id} textValue={item.formatted}>
                  {item.formatted}
                </RACListBoxItem>
              )}
            </RACListBox>
          }
          viewportClassName={styles.selectList()}
        />
      </RACPopover>
    </RACSelect>
  );
}

// Both CalendarMonthPicker and CalendarYearPicker hand back the same render-prop
// shape, so a single renderer drives both dropdowns.
type PickerRenderProps = {
  "aria-label": string;
  value: Key;
  onChange: (key: Key | null) => void;
  items: PickerItem[];
};

function renderHeaderSelect({ "aria-label": ariaLabel, value, onChange, items }: PickerRenderProps) {
  return <CalendarHeaderSelect ariaLabel={ariaLabel} items={items} onSelectionChange={onChange} selectedKey={value} />;
}

export interface CalendarMonthYearNavProps {
  /** Number of year options to show in the year dropdown. Defaults to RAC's default (20). */
  visibleYears?: number;
}

/**
 * Calendar header with month + year dropdowns for fast navigation, plus
 * previous/next month arrows. Renders inside a `Calendar` or `RangeCalendar`
 * (the month/year pickers read the calendar state from context).
 */
export function CalendarMonthYearNav({ visibleYears }: CalendarMonthYearNavProps) {
  const styles = calendarMonthYearNav();

  return (
    <header className={styles.header()}>
      <Button aria-label={t("components.calendar.previous_month")} className={styles.navButton()} slot="previous">
        <Icon name="left" />
      </Button>
      <div className={styles.pickers()}>
        <CalendarMonthPicker format="long">{renderHeaderSelect}</CalendarMonthPicker>
        <CalendarYearPicker visibleYears={visibleYears}>{renderHeaderSelect}</CalendarYearPicker>
      </div>
      <Button aria-label={t("components.calendar.next_month")} className={styles.navButton()} slot="next">
        <Icon name="right" />
      </Button>
    </header>
  );
}
